import React, { useState } from 'react';
import convert from 'convert-units';
import Breadcrumbs from 'components/Breadcrumbs';
import { calculateVentilation as calculateVentilationAPI } from 'utils/methods/calculators';

const VentilationCalculator = () => {
  const [activeTab, setActiveTab] = useState(0);  const [formData, setFormData] = useState({
    // Enclosure conditions
    volume: '',
    volumeUnit: 'm3',
    length: '',
    breadth: '',
    height: '',
    mixingCondition: 3,
    
    // Chemical details
    chemicalName: '',
    molecularWeight: '',
    physicalState: 'solid',
    
    // Process details (solid)
    dustiness: 1,
    minimumExplosiveConcentration: '',
    targetConcentration: '',
    materialHandlingActivity: 0.3,
    materialHandlingEnergy: 0.1,
    m_rate: '',
    m_rateUnit: 'kg/s',
    e_factor: '',
    // Process details (liquid)
    storagePressure: '',
    storagePressureUnit: 'Pa',
    storageTemperature: '',
    storageTemperatureUnit: 'C',
    evaporatingSurfaceArea: '',
    evaporatingSurfaceAreaUnit: 'm2',
    saturatedPressure: '',
    saturatedPressureUnit: 'bar',
    
    // Process details (gas)
    gasReleaseRate: '',
    gasReleaseRateUnit: 'm3/s',
    nozzleDiameter: '',
    nozzleDiameterUnit: 'cm',
    LEL: '',
    TLV: '',
    particleSize: ''
  });

  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [resultVolumeFlowRateUnit, setResultVolumeFlowRateUnit] = useState('m3/min');
  const assumptions = [
    'Applicable only for dilution ventilation i.e., for open area or room ventilation. Not suitable for local ventilation such as laboratory hoods',
    'Assumes rectangular buildings with uniform wind pressure coefficients (Cp) for rectangular buildings',
    'Assumes uniform air distribution within spaces and ignore transient effects',
    'Does not model short-term wind gusts or rapid temperature changes',
    'Not recommended for hazardous environments requiring precise airflow control'
  ]
  
  const tabs = [
    'Enclosure Conditions',
    'Chemical Details', 
    'Process Details',
    'Review & Calculate',
    'Results'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Handle physical state changes
      if (field === 'physicalState') {
        if (value === 'liquid') {
          // Set default values for liquid state only
          if (!prev.storageTemperature || prev.storageTemperature === '') {
            newData.storageTemperature = '25';
            newData.storageTemperatureUnit = 'C';
          }
          if (!prev.storagePressure || prev.storagePressure === '') {
            newData.storagePressure = '101325';
            newData.storagePressureUnit = 'Pa';
          }
          // Clear solid-specific fields
          newData.m_rate = '';
          // Reset gas release rate unit to default
          newData.gasReleaseRateUnit = 'm3/s';
        } else if (value === 'gas') {
          // Clear default values for gas state - let user enter their own
          newData.storageTemperature = '';
          newData.storagePressure = '';
          // Clear solid-specific fields
          newData.m_rate = '';
          // Reset gas release rate unit to default
          newData.gasReleaseRateUnit = 'm3/s';
        } else if (value === 'solid') {
          // Clear values for solid state as they're not needed
          newData.storageTemperature = '';
          newData.storagePressure = '';
          newData.evaporatingSurfaceArea = '';
          newData.saturatedPressure = '';
          newData.gasReleaseRate = '';
          newData.gasReleaseRateUnit = 'm3/s';
          newData.nozzleDiameter = '';
        }
      }
      
      // If volume is manually entered, clear dimensions to avoid confusion
      if (field === 'volume' && value) {
        newData.length = '';
        newData.lengthUnit = 'm';
        newData.breadth = '';
        newData.breadthUnit = 'm';
        newData.height = '';
        newData.heightUnit = 'm';
      }
      
      return newData;
    });
  };

  const handleUnitChange = (field, newUnit) => {
    // Call the appropriate conversion function based on the field
    switch (field) {
      case 'volumeUnit':
        updateVolume(newUnit);
        break;
      case 'storagePressureUnit':
        updateStoragePressure(newUnit);
        break;
      case 'storageTemperatureUnit':
        updateStorageTemperature(newUnit);
        break;
      case 'evaporatingSurfaceAreaUnit':
        updateEvaporatingSurfaceArea(newUnit);
        break;
      case 'saturatedPressureUnit':
        updateSaturatedPressure(newUnit);
        break;
      case 'nozzleDiameterUnit':
        updateNozzleDiameter(newUnit);
        break;
      case 'm_rateUnit':
        updateMaterialHandlingRate(newUnit);
        break;
      case 'gasReleaseRateUnit':
        updateGasReleaseRate(newUnit);
        break;
      case 'lengthUnit':
        updateLength(newUnit);
        break;
      case 'breadthUnit':
        updateBreadth(newUnit);
        break;
      case 'heightUnit':
        updateHeight(newUnit);
        break;
      default:
        // For fields without conversion, just update the unit
        handleInputChange(field, newUnit);
        break;
    }
  };

  // Unit conversion handlers
  const updateVolume = (newUnit) => {
    // If volume is entered directly, convert it
    if (formData.volume && formData.volume !== '' && (!formData.length || !formData.breadth || !formData.height)) {
      const convertedValue = convertUnits(formData.volume, formData.volumeUnit, newUnit);
      setFormData(prev => ({
        ...prev,
        volume: convertedValue.toPrecision(4),
        volumeUnit: newUnit
      }));
    } else if (formData.length && formData.breadth && formData.height) {
      // If volume is calculated from dimensions, convert each dimension to meters first
      const lengthInM = convertUnits(formData.length, formData.lengthUnit, 'm');
      const breadthInM = convertUnits(formData.breadth, formData.breadthUnit, 'm');
      const heightInM = convertUnits(formData.height, formData.heightUnit, 'm');
      const volumeInM3 = lengthInM * breadthInM * heightInM;
      const convertedVolume = convertUnits(volumeInM3, 'm3', newUnit);
      setFormData(prev => ({
        ...prev,
        volume: convertedVolume.toFixed(2),
        volumeUnit: newUnit
      }));
    } else {
      // Just update the unit if no volume is set
      setFormData(prev => ({
        ...prev,
        volumeUnit: newUnit
      }));
    }
  };

  const updateStoragePressure = (newUnit) => {
    if (!formData.storagePressure || formData.storagePressure === '') {
      setFormData(prev => ({
        ...prev,
        storagePressureUnit: newUnit
      }));
      return;
    }
    const convertedValue = convertUnits(formData.storagePressure, formData.storagePressureUnit, newUnit);
    setFormData(prev => ({
      ...prev,
      storagePressure: convertedValue.toPrecision(4),
      storagePressureUnit: newUnit
    }));
  };

  const updateStorageTemperature = (newUnit) => {
    if (!formData.storageTemperature || formData.storageTemperature === '') {
      setFormData(prev => ({
        ...prev,
        storageTemperatureUnit: newUnit
      }));
      return;
    }
    const convertedValue = convertUnits(formData.storageTemperature, formData.storageTemperatureUnit, newUnit);
    setFormData(prev => ({
      ...prev,
      storageTemperature: convertedValue.toPrecision(4),
      storageTemperatureUnit: newUnit
    }));
  };

  const updateEvaporatingSurfaceArea = (newUnit) => {
    if (!formData.evaporatingSurfaceArea || formData.evaporatingSurfaceArea === '') {
      setFormData(prev => ({
        ...prev,
        evaporatingSurfaceAreaUnit: newUnit
      }));
      return;
    }
    const convertedValue = convertUnits(formData.evaporatingSurfaceArea, formData.evaporatingSurfaceAreaUnit, newUnit);
    setFormData(prev => ({
      ...prev,
      evaporatingSurfaceArea: convertedValue.toPrecision(4),
      evaporatingSurfaceAreaUnit: newUnit
    }));
  };

  const updateSaturatedPressure = (newUnit) => {
    if (!formData.saturatedPressure || formData.saturatedPressure === '') {
      setFormData(prev => ({
        ...prev,
        saturatedPressureUnit: newUnit
      }));
      return;
    }
    const convertedValue = convertUnits(formData.saturatedPressure, formData.saturatedPressureUnit, newUnit);
    setFormData(prev => ({
      ...prev,
      saturatedPressure: convertedValue.toPrecision(4),
      saturatedPressureUnit: newUnit
    }));
  };

  const updateNozzleDiameter = (newUnit) => {
    if (!formData.nozzleDiameter || formData.nozzleDiameter === '') {
      setFormData(prev => ({
        ...prev,
        nozzleDiameterUnit: newUnit
      }));
      return;
    }
    const convertedValue = convertUnits(formData.nozzleDiameter, formData.nozzleDiameterUnit, newUnit);
    setFormData(prev => ({
      ...prev,
      nozzleDiameter: convertedValue.toPrecision(4),
      nozzleDiameterUnit: newUnit
    }));
  };

  const updateMaterialHandlingRate = (newUnit) => {
    if (!formData.m_rate || formData.m_rate === '') {
      setFormData(prev => ({
        ...prev,
        m_rateUnit: newUnit
      }));
      return;
    }
    const convertedValue = convertUnits(formData.m_rate, formData.m_rateUnit, newUnit);
    setFormData(prev => ({
      ...prev,
      m_rate: convertedValue.toPrecision(4),
      m_rateUnit: newUnit
    }));
  };

  const updateGasReleaseRate = (newUnit) => {
    if (!formData.gasReleaseRate || formData.gasReleaseRate === '') {
      setFormData(prev => ({
        ...prev,
        gasReleaseRateUnit: newUnit
      }));
      return;
    }
    const convertedValue = convertUnits(formData.gasReleaseRate, formData.gasReleaseRateUnit, newUnit);
    setFormData(prev => ({
      ...prev,
      gasReleaseRate: convertedValue.toPrecision(4),
      gasReleaseRateUnit: newUnit
    }));
  };

  const updateLength = (newUnit) => {
    if (!formData.length || formData.length === '') {
      setFormData(prev => ({
        ...prev,
        lengthUnit: newUnit
      }));
      return;
    }
    const convertedValue = convertUnits(formData.length, formData.lengthUnit, newUnit);
    setFormData(prev => ({
      ...prev,
      length: convertedValue.toPrecision(4),
      lengthUnit: newUnit
    }));
  };

  const updateBreadth = (newUnit) => {
    if (!formData.breadth || formData.breadth === '') {
      setFormData(prev => ({
        ...prev,
        breadthUnit: newUnit
      }));
      return;
    }
    const convertedValue = convertUnits(formData.breadth, formData.breadthUnit, newUnit);
    setFormData(prev => ({
      ...prev,
      breadth: convertedValue.toPrecision(4),
      breadthUnit: newUnit
    }));
  };

  const updateHeight = (newUnit) => {
    if (!formData.height || formData.height === '') {
      setFormData(prev => ({
        ...prev,
        heightUnit: newUnit
      }));
      return;
    }
    const convertedValue = convertUnits(formData.height, formData.heightUnit, newUnit);
    setFormData(prev => ({
      ...prev,
      height: convertedValue.toPrecision(4),
      heightUnit: newUnit
    }));
  };

  const updateResultWithRateUnit = (newUnit) => {
    const convertedValue = convertUnits(results?.ventilationRate || 0, resultVolumeFlowRateUnit, newUnit);
    setResults(prev => ({
      ...prev,
      ventilationRate: convertedValue.toFixed(2)
    }));
    setResultVolumeFlowRateUnit(newUnit);
  };

  const formatVolumeUnit = (unit) => {
    const unitMap = {
      'm3': 'm³',
      'km3': 'km³',
      'ft3': 'ft³',
      'l': 'L',
      'kl': 'kL',
      'gal': 'gal'
    };
    return unitMap[unit] || unit;
  };

  const formatResultVolumeFlowRateUnit = (unit) => {
    const unitMap = {
      'm3/min': 'm³/min',
      'm3/h': 'm³/hr', 
      'm3/s': 'm³/sec',
      'l/min': 'L/min',
      'l/h': 'L/hr',
      'l/s': 'L/sec',
      'ft3/min': 'ft³/min',
      'ft3/h': 'ft³/hr',
      'kl/h': 'kL/hr',
      'kl/s': 'kL/sec',
      'kl/min': 'kL/min'
    };
    return unitMap[unit] || unit;
  };

  const calculateVolume = () => {
    if (formData.length && formData.breadth && formData.height) {
      // Convert each dimension to meters first
      const lengthInM = convertUnits(formData.length, formData.lengthUnit, 'm');
      const breadthInM = convertUnits(formData.breadth, formData.breadthUnit, 'm');
      const heightInM = convertUnits(formData.height, formData.heightUnit, 'm');
      
      // Calculate volume in m³
      const volumeInM3 = lengthInM * breadthInM * heightInM;
      
      // Convert to the selected volume unit and return as string
      const convertedVolume = convertUnits(volumeInM3, 'm3', formData.volumeUnit);
      return convertedVolume.toFixed(2);
    }
    return formData.volume;
  };

  // Auto-calculate volume when dimensions change
  const handleDimensionChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    
    // Update the field first
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // If all dimensions are provided, auto-calculate volume
    if (newData.length && newData.breadth && newData.height) {
      // Calculate volume in m³ first (since dimensions are in meters)
      const volumeInM3 = (parseFloat(newData.length) * parseFloat(newData.breadth) * parseFloat(newData.height));
      
      // Convert to the selected volume unit
      const convertedVolume = convertUnits(volumeInM3, 'm3', formData.volumeUnit);
      
      setFormData(prev => ({ ...prev, volume: convertedVolume.toFixed(2) }));
    }
  };

  const nextTab = () => {
    if (activeTab < tabs.length - 1) {
      setActiveTab(activeTab + 1);
    }
  };

  const prevTab = () => {
    if (activeTab > 0) {
      setActiveTab(activeTab - 1);
    }
  };

  // Convert all form inputs to standard units for API call
  const convertToStandardUnits = () => {
    const standardData = { ...formData };

    // Convert volume to m³ (if using volume input)
    if (formData.volume && formData.volumeUnit !== 'm3') {
      standardData.volume = convertUnits(formData.volume, formData.volumeUnit, 'm3');
    }

    // Convert storage pressure to bar
    if (formData.storagePressure && formData.storagePressureUnit !== 'bar') {
      standardData.storagePressure = convertUnits(formData.storagePressure, formData.storagePressureUnit, 'bar');
    }

    // Convert storage temperature to K
    if (formData.storageTemperature && formData.storageTemperatureUnit !== 'K') {
      standardData.storageTemperature = convertUnits(formData.storageTemperature, formData.storageTemperatureUnit, 'K');
    }

    // Convert evaporating surface area to m²
    if (formData.evaporatingSurfaceArea && formData.evaporatingSurfaceAreaUnit !== 'm2') {
      standardData.evaporatingSurfaceArea = convertUnits(formData.evaporatingSurfaceArea, formData.evaporatingSurfaceAreaUnit, 'm2');
    }

    // Convert saturated pressure to bar (handle different pressure unit cases)
    if (formData.saturatedPressure && formData.saturatedPressureUnit !== 'bar') {
      let pressureUnit = formData.saturatedPressureUnit;
      // Handle case sensitivity for pressure units
      if (pressureUnit === 'pa') pressureUnit = 'Pa';
      if (pressureUnit === 'kpa') pressureUnit = 'kPa';
      if (pressureUnit === 'mpa') pressureUnit = 'MPa';
      standardData.saturatedPressure = convertUnits(formData.saturatedPressure, pressureUnit, 'bar');
    }

    // Convert nozzle diameter to m
    if (formData.nozzleDiameter && formData.nozzleDiameterUnit !== 'm') {
      standardData.nozzleDiameter = convertUnits(formData.nozzleDiameter, formData.nozzleDiameterUnit, 'm');
    }

    // Convert material handling rate to kg/s
    if (formData.m_rate && formData.m_rateUnit !== 'kg/s') {
      standardData.m_rate = convertUnits(formData.m_rate, formData.m_rateUnit, 'kg/s');
    }

    // Convert gas release rate to m³/s
    if (formData.gasReleaseRate && formData.gasReleaseRateUnit !== 'm3/s') {
      standardData.gasReleaseRate = convertUnits(formData.gasReleaseRate, formData.gasReleaseRateUnit, 'm3/s');
    }

    return standardData;
  };
  const calculateVentilation = async () => {
    if (!inputValidation()) {
      alert('Please fill in all required fields.');
      return;
    }

    try {
      // Convert all inputs to standard units before API call
      const standardData = convertToStandardUnits();

      // Prepare the request data according to the backend API with standard units
      const requestData = {
        container: {
          length: standardData.length ? parseFloat(standardData.length) : null,
          breadth: standardData.breadth ? parseFloat(standardData.breadth) : null,
          height: standardData.height ? parseFloat(standardData.height) : null,
          volume: standardData.volume ? parseFloat(standardData.volume) : null
        },
        mixingCondition: parseInt(standardData.mixingCondition),
        chemical: {
          name: standardData.chemicalName,
          molecularWeight: parseFloat(standardData.molecularWeight)
        },
        dustiness: parseInt(standardData.dustiness),
        storagePressure: parseFloat(standardData.storagePressure) || 0,
        storageTemperature: parseFloat(standardData.storageTemperature) || 0,
        nozzleDiameter: parseFloat(standardData.nozzleDiameter) || 0,
        lel: parseFloat(standardData.LEL) || 0,
        tlv: parseFloat(standardData.TLV) || 0,
        evaporatingSurfaceArea: parseFloat(standardData.evaporatingSurfaceArea) || 0,
        saturatedPressure: parseFloat(standardData.saturatedPressure) || 0,
        particleSize: parseFloat(standardData.particleSize) || 0,
        minimumExplosiveConcentration: parseFloat(standardData.minimumExplosiveConcentration) || 0,
        targetConcentration: parseFloat(standardData.targetConcentration),
        materialHandlingActivity: parseFloat(standardData.materialHandlingActivity) || 0,
        materialHandlingEnergy: parseFloat(standardData.materialHandlingEnergy) || 0,
        m_rate: parseFloat(standardData.m_rate) || 0,
        e_factor: parseFloat(standardData.e_factor) || 0,
        gasReleaseRate: parseFloat(standardData.gasReleaseRate) || 0,
        chemicalPhysicalState: standardData.physicalState
      };

      console.log('Sending request:', requestData);

      // Make API call using the established methods approach
      const response = await calculateVentilationAPI(requestData);
      const result = response.data;
      console.log('API Response:', result);

      if (result.status === 'success') {
        const calculatedVolume = calculateVolume() || formData.volume;
        
        // Convert the ventilation rate from API (m3/s) to the current display unit
        const apiVentilationRate = parseFloat(result.data.ventilation_rate);
        const displayVentilationRate = convertUnits(apiVentilationRate, 'm3/s', resultVolumeFlowRateUnit);
        
        setResults({
          ventilationRate: displayVentilationRate.toFixed(2),
          airChangesPerHour: (result.data.ventilation_rate / calculatedVolume * 60).toFixed(2),
          volume: calculatedVolume,
          containerVolume: result.data.container_volume,
          calculationParameters: result.data.calculation_parameters,
        });
        
        setShowResults(true);
        // Navigate to results tab
        setActiveTab(4);
      } else {
        alert('Calculation failed: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error calculating ventilation:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Network error occurred';
      alert('Error calculating ventilation: ' + errorMessage);
    }
  };

  // Helper calculation functions using convert-units package
  const convertUnits = (value, fromUnit, toUnit) => {
    if (fromUnit === toUnit) return parseFloat(value);
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 0;
    
    try {
      // Handle unit mapping for convert-units package
      const unitMapping = {
        // Volume units
        'm3': 'm3',
        'km3': 'km3', 
        'ft3': 'ft3',
        'l': 'l',
        'kl': 'kl',
        'gal': 'gal',
        
        // Volume flow rate units (convert-units package uses different naming)
        'm3/s': 'm3/s',
        'm3/min': 'm3/min',
        'm3/h': 'm3/h',
        'l/s': 'l/s',
        'l/min': 'l/min', 
        'l/h': 'l/h',
        'ft3/s': 'ft3/s',
        'ft3/min': 'ft3/min',
        'ft3/h': 'ft3/h',
        'kl/s': 'kl/s',
        'kl/min': 'kl/min',
        'kl/h': 'kl/h',
        
        // Mass flow rate units
        'kg/s': 'kg/s',
        'kg/min': 'kg/min',
        'kg/h': 'kg/h',
        'g/s': 'g/s',
        'g/min': 'g/min',
        'g/h': 'g/h',
        'lb/s': 'lb/s',
        'lb/min': 'lb/min',
        'lb/h': 'lb/h',
        
        // Temperature units
        'K': 'K',
        'C': 'C',
        'F': 'F',
        
        // Pressure units  
        'bar': 'bar',
        'pa': 'Pa',
        'kpa': 'kPa',
        'mpa': 'MPa',
        'psi': 'psi',
        
        // Area units
        'm2': 'm2',
        'cm2': 'cm2',
        'ft2': 'ft2',
        'in2': 'in2',
        
        // Length units
        'm': 'm',
        'cm': 'cm',
        'mm': 'mm',
        'ft': 'ft',
        'in': 'in'
      };
      
      const mappedFromUnit = unitMapping[fromUnit] || fromUnit;
      const mappedToUnit = unitMapping[toUnit] || toUnit;
      
      return convert(numValue).from(mappedFromUnit).to(mappedToUnit);
    } catch (error) {
      console.warn(`Unit conversion failed from ${fromUnit} to ${toUnit}:`, error);
      // Fallback to custom conversion for unsupported units
      return customConvertUnits(numValue, fromUnit, toUnit);
    }
  };

  // Fallback custom conversion for units not supported by convert-units
  const customConvertUnits = (value, fromUnit, toUnit) => {
    const conversionFactors = {
      // Volume
      'm3': 1,
      'km3': 1000000,
      'ft3': 0.0283168,
      'l': 0.001,
      'kl': 1,
      'gal': 0.00378541,
      // Volume flow rates (all to m3/s base)
      'm3/s': 1,
      'm3/min': 1/60,
      'm3/h': 1/3600,
      'l/s': 0.001,
      'l/min': 0.001/60,
      'l/h': 0.001/3600,
      'ft3/s': 0.0283168,
      'ft3/min': 0.0283168/60,
      'ft3/h': 0.0283168/3600,
      'kl/s': 1,
      'kl/min': 1/60,
      'kl/h': 1/3600,
      // Mass flow rates (all to kg/s base)
      'kg/s': 1,
      'kg/min': 1/60,
      'kg/h': 1/3600,
      'g/s': 0.001,
      'g/min': 0.001/60,
      'g/h': 0.001/3600,
      'lb/s': 0.453592,
      'lb/min': 0.453592/60,
      'lb/h': 0.453592/3600,
      // Temperature
      'K': 1,
      'C': (val) => val + 273.15,
      'F': (val) => (val - 32) * 5/9 + 273.15,
      // Pressure
      'bar': 1,
      'pa': 0.00001,
      'kpa': 0.01,
      'mpa': 10,
      'psi': 0.0689476,
      // Area
      'm2': 1,
      'cm2': 0.0001,
      'ft2': 0.092903,
      'in2': 0.00064516,
      // Length
      'm': 1,
      'cm': 0.01,
      'mm': 0.001,
      'ft': 0.3048,
      'in': 0.0254
    };
    
    // Handle temperature conversions specially
    if (['K', 'C', 'F'].includes(fromUnit) && ['K', 'C', 'F'].includes(toUnit)) {
      let kelvinValue = parseFloat(value);
      
      if (fromUnit === 'C') kelvinValue = parseFloat(value) + 273.15;
      if (fromUnit === 'F') kelvinValue = (parseFloat(value) - 32) * 5/9 + 273.15;
      
      if (toUnit === 'C') return kelvinValue - 273.15;
      if (toUnit === 'F') return (kelvinValue - 273.15) * 9/5 + 32;
      
      return kelvinValue; // K to K
    }
    
    const fromFactor = conversionFactors[fromUnit] || 1;
    const toFactor = conversionFactors[toUnit] || 1;
    
    return (parseFloat(value) * fromFactor) / toFactor;
  };

  const heatTransferCoefficient = () => {
    const M = formData.molecularWeight;
    const k = Math.cbrt(0.83 * (18/M));
    return k;
  };

  const gasConstant = () => {
    const Rg = 8.314 * (formData.molecularWeight / 1000);
    return Rg;
  };

  const evaporationRate = () => {
    const M = formData.molecularWeight;
    const k = heatTransferCoefficient();
    const Psat = convertUnits(formData.saturatedPressure, formData.saturatedPressureUnit, 'bar');
    const Rg = gasConstant();
    const A = convertUnits(formData.evaporatingSurfaceArea, formData.evaporatingSurfaceAreaUnit, 'm2');
    const T = convertUnits(formData.storageTemperature, formData.storageTemperatureUnit, 'K');

    const Qm = (M*k*A*Psat)/(Rg*T);
    return Qm;
  };

  const ventilationRate = (G, K, Ctarget, Qm=1, T=1, Rg=1, P=1, M=1) => {
    const Q = (G * Qm * K * T * Rg * (10**6)) / (Ctarget * P * M);
    return Q.toFixed(2);
  };

  const dustGenerationRate = () => {
    const A = formData.materialHandlingActivity;
    const D = formData.dustiness;
    const E = formData.materialHandlingEnergy;
    const M_rate = convertUnits(formData.m_rate, formData.m_rateUnit, 'kg/s');

    const G = A*D*E*M_rate;
    return G;
  };

  const inputValidation = () => {
    // Common validations for all physical states
    if (!formData.molecularWeight || !formData.physicalState || !formData.targetConcentration) {
      return false;
    }

    // Container validation - volume is mandatory (either entered directly or calculated from dimensions)
    const currentVolume = calculateVolume();
    if (!currentVolume || parseFloat(currentVolume) <= 0) {
      return false;
    }

    // Validation for mixing condition
    if (!formData.mixingCondition) {
      return false;
    }
    
    // State-specific validations
    switch (formData.physicalState) {
      case 'solid':
        if (!formData.dustiness || !formData.materialHandlingActivity || !formData.materialHandlingEnergy || !formData.m_rate) {
          return false;
        }
        break;
      
      case 'liquid':
        if (!formData.storagePressure || !formData.storageTemperature || 
            !formData.evaporatingSurfaceArea || !formData.saturatedPressure) {
          return false;
        }
        break;
      
      case 'gas':
        if (!formData.storagePressure || !formData.storageTemperature || !formData.gasReleaseRate) {
          return false;
        }
        break;
      
      default:
        return false;
    }

    return true;
  };

  const handleMECInput = (event) => {
    const value = parseFloat(event.target.value);
    console.log('MEC Input value:', value);
    
    if (isNaN(value) || value <= 0) {
      console.log('MEC value is invalid or zero');
      return;
    }
    
    const mw = parseFloat(formData.molecularWeight);
    console.log('Molecular weight:', mw);
    
    if (isNaN(mw) || mw <= 0) {
      alert('Please enter a valid molecular weight.');
      return;
    }
    
    const targetConc = value * 0.25 * 24.45 / mw;
    console.log('Calculated target concentration:', targetConc);
    
    handleInputChange('targetConcentration', targetConc);
  };

  const handleLELInput = (event) => {
    const value = parseFloat(event.target.value);
    console.log('LEL Input value:', value);
    
    if (isNaN(value) || value <= 0) {
      console.log('LEL value is invalid or zero');
      return;
    }
    
    const targetConc = value * 0.25 * 10000;
    console.log('Calculated target concentration from LEL:', targetConc);
    
    handleInputChange('targetConcentration', targetConc);
  };

  const handleTLVInput = (event) => {
    const value = parseFloat(event.target.value);
    console.log('TLV Input value:', value);
    
    if (isNaN(value) || value <= 0) {
      console.log('TLV value is invalid or zero');
      return;
    }
    
    // TLV target concentration is simply 10% (0.1) of the TLV value
    const targetConc = value * 0.1;
    console.log('Calculated target concentration from TLV (10%):', targetConc);
    
    handleInputChange('targetConcentration', targetConc);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white via-blue-50 to-blue-100">
      {/* Main Content */}
      <div className="px-4 py-8">
        <div className="w-full mx-auto">
          {/* Header Section */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Ventilation Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Calculate the ventilation rate required for safe chemical operations.
            </p>
          </div>
          <Breadcrumbs />
          
          {/* Full Width Layout */}
          <div className="w-full">
            {/* Main Form Container */}
            <div className="w-full bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-6 md:p-8">
                <form onSubmit={(e) => { e.preventDefault(); calculateVentilation(); }} className="space-y-8">
                  {/* Tab Navigation */}
                    {/* Assumptions & Limitations */}
                    <div className="bg-yellow-50 p-6 rounded-lg">
                      <h4 className="font-medium text-yellow-800 mb-3">Assumptions & Limitations:</h4>
                      <ul className="space-y-2">
                        {assumptions.map((assumption, index) => (
                          <li key={index} className="flex items-start text-sm text-yellow-700">
                            <i className="fas fa-info-circle text-yellow-600 mr-2 mt-0.5 flex-shrink-0"></i>
                            {assumption}
                          </li>
                        ))}
                      </ul>
                    </div>
                  <div className="mb-8">
                    <div className="flex flex-wrap border-b border-gray-200 pb-4 gap-2 flex-col md:flex-row">
                      {tabs.map((tab, index) => (
                        <button 
                          key={index}
                          type="button"
                          onClick={() => setActiveTab(index)}
                          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors mr-2 ${
                            activeTab === index 
                              ? 'bg-blue-600 text-white border-b-2 border-blue-600' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <span className="mr-1">{index + 1}.</span> {tab}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Tab Content Container */}
                  <div className="tab-content">
                    {/* Tab 1: Enclosure Conditions */}
                    {activeTab === 0 && (
                      <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                          Enclosure conditions
                        </h2>
                        
                        <div className="space-y-6">
                          <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow grid lg:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-lg font-bold text-gray-700 mb-2">
                                Volume
                                <span className="text-red-500">*</span>
                              </label>
                              <div className="relative flex items-center gap-4 flex-col md:flex-row">
                                <input
                                  value={formData.volume}
                                  onChange={(e) => handleInputChange('volume', e.target.value)}
                                  type="number"
                                  required
                                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pl-4 pr-12 transition-all hover:border-blue-300"
                                  placeholder="Enter volume or use dimensions below"
                                />
                                <select 
                                  className="min-w-20 w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" 
                                  value={formData.volumeUnit}
                                  onChange={(e) => handleUnitChange('volumeUnit', e.target.value)}
                                >
                                  <option value="m3">m³</option>
                                  <option value="km3">km³</option>
                                  <option value="ft3">ft³</option>
                                  <option value="l">Liters</option>
                                  <option value="kl">Kiloliters</option>
                                  <option value="gal">Gallons</option>
                                </select>
                              </div>
                              <p className="mt-2 text-sm text-gray-500">
                                Enter the volume directly or use the dimensions to calculate it automatically.
                              </p>
                            </div>


                            {/* <div className="my-6 text-center text-gray-500 font-medium">OR calculate from dimensions</div> */}

                            <div>
                              <label className="block text-lg font-bold text-gray-700 mb-2">
                                Enter dimensions (will auto-calculate volume)
                              </label>
                              <p className="text-sm text-gray-500 mb-3">
                                Enter dimensions in any unit. Volume will be calculated and converted to the selected volume unit.
                              </p>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                  <span className="text-gray-500 text-sm font-medium">Length</span>
                                  <div className="flex gap-2 flex-col md:flex-row">
                                    <input
                                      value={formData.length}
                                      onChange={(e) => handleDimensionChange('length', e.target.value)}
                                      type="number"
                                      className="flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-all hover:border-blue-300 w-full"
                                      placeholder="Length"
                                    />
                                    <select 
                                      className="min-w-10 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm" 
                                      value={formData.lengthUnit}
                                      onChange={(e) => handleUnitChange('lengthUnit', e.target.value)}
                                    >
                                      <option value="m">m</option>
                                      <option value="cm">cm</option>
                                      <option value="mm">mm</option>
                                      <option value="ft">ft</option>
                                      <option value="in">in</option>
                                    </select>
                                  </div>
                                </div>
                                
                                <div className="space-y-2">
                                  <span className="text-gray-500 text-sm font-medium">Breadth</span>
                                  <div className="flex gap-2 flex-col md:flex-row">
                                    <input
                                      value={formData.breadth}
                                      onChange={(e) => handleDimensionChange('breadth', e.target.value)}
                                      type="number"
                                      className="flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-all hover:border-blue-300 w-full"
                                      placeholder="Breadth"
                                    />
                                    <select 
                                      className="min-w-10 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm" 
                                      value={formData.breadthUnit}
                                      onChange={(e) => handleUnitChange('breadthUnit', e.target.value)}
                                    >
                                      <option value="m">m</option>
                                      <option value="cm">cm</option>
                                      <option value="mm">mm</option>
                                      <option value="ft">ft</option>
                                      <option value="in">in</option>
                                    </select>
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <span className="text-gray-500 text-sm font-medium">Height</span>
                                  <div className="flex gap-2 flex-col md:flex-row">
                                    <input
                                      value={formData.height}
                                      onChange={(e) => handleDimensionChange('height', e.target.value)}
                                      type="number"
                                      className="flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-all hover:border-blue-300 w-full"
                                      placeholder="Height"
                                    />
                                    <select 
                                      className="min-w-10 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm" 
                                      value={formData.heightUnit}
                                      onChange={(e) => handleUnitChange('heightUnit', e.target.value)}
                                    >
                                      <option value="m">m</option>
                                      <option value="cm">cm</option>
                                      <option value="mm">mm</option>
                                      <option value="ft">ft</option>
                                      <option value="in">in</option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Show calculated volume if dimensions are provided */}
                            {formData.length && formData.breadth && formData.height && (
                              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                                <p className="text-sm text-blue-800">
                                  <span className="font-medium">Calculated Volume:</span> {calculateVolume()} {formatVolumeUnit(formData.volumeUnit)}
                                </p>
                                <p className="text-xs text-blue-600 mt-1">
                                  From dimensions: {formData.length}{formData.lengthUnit} × {formData.breadth}{formData.breadthUnit} × {formData.height}{formData.heightUnit}
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                            <label className="block text-lg font-bold text-gray-700 mb-2">
                              Mixing conditions
                              <span className="text-red-500">*</span>
                            </label>
                            <select 
                              value={formData.mixingCondition}
                              onChange={(e) => handleInputChange('mixingCondition', parseInt(e.target.value))}
                              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                            >
                              <option value={1}>Ideal (Good mixing - Forced air circulation)</option>
                              <option value={3}>Moderate (Partial natural ventilation)</option>
                              <option value={10}>Poor (Passive systems)</option>
                            </select>
                          </div>
                        </div>
                        
                        {/* Navigation Buttons */}
                        <div className="flex justify-between mt-8">
                          <button 
                            type="button" 
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors opacity-50 cursor-not-allowed"
                            disabled
                          >
                            Previous
                          </button>
                          <button 
                            type="button" 
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            onClick={nextTab}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Tab 2: Chemical Details */}
                    {activeTab === 1 && (
                      <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                          Chemical Details
                        </h2>
                        
                        <div className="space-y-6">

                          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>

                            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                              <label className="block text-lg font-bold text-gray-700 mb-2">
                                Chemical Name
                              </label>
                              <div className="relative">
                                <input
                                  value={formData.chemicalName}
                                  onChange={(e) => handleInputChange('chemicalName', e.target.value)}
                                  type="text"
                                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pl-4 pr-12 transition-all hover:border-blue-300"
                                  placeholder="Enter chemical name (optional)"
                                />
                              </div>
                            </div>

                            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                              <label className="block text-lg font-bold text-gray-700 mb-2">
                                Molecular weight
                                <span className="text-red-500">*</span>
                              </label>
                              <div className="relative">
                                <input
                                  value={formData.molecularWeight}
                                  onChange={(e) => handleInputChange('molecularWeight', e.target.value)}
                                  type="number"
                                  step="0.00001"
                                  required 
                                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pl-4 pr-12 transition-all hover:border-blue-300"
                                  placeholder="Enter chemical's molecular weight"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">g/mol</span>
                              </div>
                              <p className="mt-2 text-sm text-gray-500">Enter the molecular weight of the chemical in the given units</p>
                            </div>
                          </div>
                          

                          <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                            <label className="block text-lg font-bold text-gray-700 mb-2">
                              Physical state
                              <span className="text-red-500">*</span>
                            </label>
                            <select 
                              value={formData.physicalState}
                              onChange={(e) => handleInputChange('physicalState', e.target.value)}
                              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                            >
                              <option value="solid">Solid</option>
                              <option value="liquid">Liquid</option>
                              <option value="gas">Gas</option>
                            </select>
                          </div>

                        </div>
                        
                        {/* Navigation Buttons */}
                        <div className="flex justify-between mt-8">
                          <button 
                            type="button" 
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            onClick={prevTab}
                          >
                            Previous
                          </button>
                          <button 
                            type="button" 
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            onClick={nextTab}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Tab 3: Process Details */}
                    {activeTab === 2 && (
                      <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                          Process Details
                        </h2>
                        
                        {formData.physicalState === 'solid' && (
                          <div className="space-y-6">
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>

                              <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                                <label className="block text-lg font-bold text-gray-700 mb-2">
                                  Form of solid (Dustiness)
                                  <span className="text-red-500">*</span>
                                </label>
                                <select 
                                  value={formData.dustiness}
                                  onChange={(e) => handleInputChange('dustiness', parseInt(e.target.value))}
                                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                  <option value={0.1}>Tablets</option>
                                  <option value={0.5}>Granules</option>
                                  <option value={1}>Fine Powder</option>
                                </select>
                              </div>

                              

                              <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                                <label className="block text-lg font-bold text-gray-700 mb-2">
                                  Material Handling Activity
                                  <span className="text-red-500">*</span>
                                </label>
                                <select 
                                  value={formData.materialHandlingActivity}
                                  onChange={(e) => handleInputChange('materialHandlingActivity', parseFloat(e.target.value))}
                                  required
                                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                  <option value={0.3}>Careful handling (Sealed containers)</option>
                                  <option value={0.7}>Normal Transfer (Scooping / pouring)</option>
                                  <option value={1}>Vigorous process (grinding / mixing)</option>
                                </select>
                              </div>

                              <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                                <label className="block text-lg font-bold text-gray-700 mb-2">
                                  Material Handling Energy
                                  <span className="text-red-500">*</span>
                                </label>
                                <select 
                                  value={formData.materialHandlingEnergy}
                                  onChange={(e) => handleInputChange('materialHandlingEnergy', parseFloat(e.target.value))}
                                  required
                                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                  <option value={0.1}>Low Energy (Manual handling)</option>
                                  <option value={0.5}>Medium Energy (Mechanical conveying)</option>
                                  <option value={1}>High Energy (High-speed mixing/grinding)</option>
                                </select>
                              </div>

                              <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                                <label className="block text-lg font-bold text-gray-700 mb-2">
                                  Material Handling Rate
                                  <span className="text-red-500">*</span>
                                </label>
                                <div className="flex gap-2 flex-col md:flex-row">
                                  <input
                                    value={formData.m_rate}
                                    onChange={(e) => handleInputChange('m_rate', e.target.value)}
                                    type="number"
                                    step="0.001"
                                    min="0"
                                    required
                                    placeholder="Enter material handling rate"
                                    className="flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 w-full"
                                  />
                                  <select 
                                    value={formData.m_rateUnit}
                                    onChange={(e) => handleUnitChange('m_rateUnit', e.target.value)}
                                    className="min-w-24 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                  >
                                    <option value="kg/s">kg/s</option>
                                    <option value="kg/min">kg/min</option>
                                    <option value="kg/h">kg/h</option>
                                    <option value="g/s">g/s</option>
                                    <option value="g/min">g/min</option>
                                    <option value="g/h">g/h</option>
                                    <option value="lb/s">lb/s</option>
                                    <option value="lb/min">lb/min</option>
                                    <option value="lb/h">lb/h</option>
                                  </select>
                                </div>
                              </div>

                              <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow relative">
                                <label className="block text-lg font-bold text-gray-700 mb-2">
                                  Emission Factor
                                  <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                  <input
                                    value={formData.e_factor}
                                    onChange={(e) => handleInputChange('e_factor', e.target.value)}
                                    type="number"
                                    step="0.001"
                                    min="0.01"
                                    max="0.1"
                                    required
                                    placeholder="Enter emission factor"
                                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pl-4 pr-12 transition-all hover:border-blue-300"
                                  />
                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">%</span>
                                </div>
                              </div>
                            </div>

                            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                              <label className="block text-lg font-bold text-gray-700 mb-2">
                                Target Concentration
                                <span className="text-red-500">*</span>
                              </label>

                              <div className="relative">
                                <input
                                  value={formData.targetConcentration}
                                  onChange={(e) => handleInputChange('targetConcentration', e.target.value)}
                                  type="number"
                                  step="0.00001"
                                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pl-4 pr-12 transition-all hover:border-blue-300"
                                  placeholder="Enter target concentration"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">ppm</span>
                              </div>

                              <div className="mt-4 space-y-2 grid">
                                <p className="text-sm text-gray-600">Or calculate from:</p>
                                <div className="grid grid-cols-1 gap-2">
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">MEC (mg/m³)</label>
                                    <input
                                      type="number"
                                      onChange={handleMECInput}
                                      className="w-full p-2 text-sm border rounded focus:ring-1 focus:ring-blue-500"
                                      placeholder="MEC"
                                    />
                                  </div>
                                </div>
                              </div>

                            </div>
                          </div>
                        )}

                        {(formData.physicalState === 'liquid' || formData.physicalState === 'gas') && (
                          <div className="space-y-6">
                            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                              <label className="block text-lg font-bold text-gray-700 mb-2">
                                Storage Pressure
                                <span className="text-red-500">*</span>
                              </label>
                              <div className="relative flex items-center gap-4 flex-col md:flex-row">
                                <input
                                  value={formData.storagePressure}
                                  onChange={(e) => handleInputChange('storagePressure', e.target.value)}
                                  type="number"
                                  step="0.00001"
                                  required
                                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pl-4 pr-12 transition-all hover:border-blue-300"
                                  placeholder="Enter storage pressure"
                                />
                                <select 
                                  className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white min-w-20" 
                                  value={formData.storagePressureUnit}
                                  onChange={(e) => handleUnitChange('storagePressureUnit', e.target.value)}
                                >
                                  <option value="bar">bar</option>
                                  <option value="Pa">Pa</option>
                                  <option value="kPa">kPa</option>
                                  <option value="MPa">MPa</option>
                                  <option value="psi">psi</option>
                                  <option value="torr">torr</option>
                                </select>
                              </div>
                            </div>

                            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                              <label className="block text-lg font-bold text-gray-700 mb-2">
                                Storage Temperature
                                <span className="text-red-500">*</span>
                              </label>
                              <div className="relative flex items-center gap-4 flex-col md:flex-row">
                                <input
                                  value={formData.storageTemperature}
                                  onChange={(e) => handleInputChange('storageTemperature', e.target.value)}
                                  type="number"
                                  step="0.00001"
                                  required
                                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pl-4 pr-12 transition-all hover:border-blue-300"
                                  placeholder="Enter storage temperature"
                                />
                                <select 
                                  className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" 
                                  value={formData.storageTemperatureUnit}
                                  onChange={(e) => handleUnitChange('storageTemperatureUnit', e.target.value)}
                                >
                                  <option value="K">K</option>
                                  <option value="C">°C</option>
                                  <option value="F">°F</option>
                                </select>
                              </div>
                            </div>

                            {formData.physicalState === 'liquid' && (
                              <>
                                <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                                  <label className="block text-lg font-bold text-gray-700 mb-2">
                                    Evaporating Surface Area
                                    <span className="text-red-500">*</span>
                                  </label>
                                  <div className="relative flex items-center gap-4 flex-col md:flex-row">
                                    <input
                                      value={formData.evaporatingSurfaceArea}
                                      onChange={(e) => handleInputChange('evaporatingSurfaceArea', e.target.value)}
                                      type="number"
                                      step="0.00001"
                                      required
                                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pl-4 pr-12 transition-all hover:border-blue-300"
                                      placeholder="Enter evaporating surface area"
                                    />
                                    <select 
                                      className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" 
                                      value={formData.evaporatingSurfaceAreaUnit}
                                      onChange={(e) => handleUnitChange('evaporatingSurfaceAreaUnit', e.target.value)}
                                    >
                                      <option value="cm2">cm²</option>
                                      <option value="m2">m²</option>
                                      <option value="ft2">ft²</option>
                                      <option value="in2">in²</option>
                                    </select>
                                  </div>
                                </div>

                                <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                                  <label className="block text-lg font-bold text-gray-700 mb-2">
                                    Saturated Pressure
                                    <span className="text-red-500">*</span>
                                  </label>
                                  <div className="relative flex items-center gap-4 flex-col md:flex-row">
                                    <input
                                      value={formData.saturatedPressure}
                                      onChange={(e) => handleInputChange('saturatedPressure', e.target.value)}
                                      type="number"
                                      step="0.00001"
                                      required
                                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pl-4 pr-12 transition-all hover:border-blue-300"
                                      placeholder="Enter saturated pressure"
                                    />
                                    <select 
                                      className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" 
                                      value={formData.saturatedPressureUnit}
                                      onChange={(e) => handleUnitChange('saturatedPressureUnit', e.target.value)}
                                    >
                                      <option value="bar">bar</option>
                                      <option value="Pa">Pa</option>
                                      <option value="kPa">kPa</option>
                                      <option value="MPa">MPa</option>
                                      <option value="psi">psi</option>
                                    </select>
                                  </div>
                                </div>

                                <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                                  <label className="block text-lg font-bold text-gray-700 mb-2">
                                    Nozzle Diameter (Optional)
                                  </label>
                                  <div className="relative flex items-center gap-4 flex-col md:flex-row">
                                    <input
                                      value={formData.nozzleDiameter}
                                      onChange={(e) => handleInputChange('nozzleDiameter', e.target.value)}
                                      type="number"
                                      step="0.00001"
                                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pl-4 pr-12 transition-all hover:border-blue-300"
                                      placeholder="Enter nozzle diameter (optional)"
                                    />
                                    <select 
                                      className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" 
                                      value={formData.nozzleDiameterUnit}
                                      onChange={(e) => handleUnitChange('nozzleDiameterUnit', e.target.value)}
                                    >
                                      <option value="cm">cm</option>
                                      <option value="m">m</option>
                                      <option value="in">in</option>
                                      <option value="ft">ft</option>
                                    </select>
                                  </div>
                                  <p className="mt-2 text-sm text-gray-500">Nozzle or pipe diameter for gas/liquid release (if applicable)</p>
                                </div>
                              </>
                            )}

                            {formData.physicalState === 'gas' && (
                              <>
                                <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                                  <label className="block text-lg font-bold text-gray-700 mb-2">
                                    Gas Release Rate
                                    <span className="text-red-500">*</span>
                                  </label>
                                  <div className="relative flex items-center gap-4 flex-col md:flex-row">
                                    <input
                                      value={formData.gasReleaseRate}
                                      onChange={(e) => handleInputChange('gasReleaseRate', e.target.value)}
                                      type="number"
                                      step="0.00001"
                                      required
                                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pl-4 pr-12 transition-all hover:border-blue-300"
                                      placeholder="Enter gas release rate"
                                    />
                                    <select 
                                      className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" 
                                      value={formData.gasReleaseRateUnit}
                                      onChange={(e) => handleUnitChange('gasReleaseRateUnit', e.target.value)}
                                    >
                                      <option value="m3/s">m³/s</option>
                                      <option value="m3/min">m³/min</option>
                                      <option value="m3/h">m³/h</option>
                                      <option value="l/s">L/s</option>
                                      <option value="l/min">L/min</option>
                                      <option value="l/h">L/h</option>
                                      <option value="ft3/s">ft³/s</option>
                                      <option value="ft3/min">ft³/min</option>
                                      <option value="ft3/h">ft³/h</option>
                                      <option value="kl/s">kL/s</option>
                                      <option value="kl/min">kL/min</option>
                                      <option value="kl/h">kL/h</option>
                                    </select>
                                  </div>
                                </div>

                                <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                                  <label className="block text-lg font-bold text-gray-700 mb-2">
                                    Nozzle Diameter (Optional)
                                  </label>
                                  <div className="relative flex items-center gap-4 flex-col md:flex-row">
                                    <input
                                      value={formData.nozzleDiameter}
                                      onChange={(e) => handleInputChange('nozzleDiameter', e.target.value)}
                                      type="number"
                                      step="0.00001"
                                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pl-4 pr-12 transition-all hover:border-blue-300"
                                      placeholder="Enter nozzle diameter (optional)"
                                    />
                                    <select 
                                      className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" 
                                      value={formData.nozzleDiameterUnit}
                                      onChange={(e) => handleUnitChange('nozzleDiameterUnit', e.target.value)}
                                    >
                                      <option value="cm">cm</option>
                                      <option value="m">m</option>
                                      <option value="in">in</option>
                                      <option value="ft">ft</option>
                                    </select>
                                  </div>
                                  <p className="mt-2 text-sm text-gray-500">Nozzle or pipe diameter for gas release (if applicable)</p>
                                </div>
                              </>
                            )}

                            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                              <label className="block text-lg font-bold text-gray-700 mb-2">
                                Target Concentration
                                <span className="text-red-500">*</span>
                              </label>
                              <div className="relative">
                                <input
                                  value={formData.targetConcentration}
                                  onChange={(e) => handleInputChange('targetConcentration', e.target.value)}
                                  type="number"
                                  step="0.00001"
                                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pl-4 pr-12 transition-all hover:border-blue-300"
                                  placeholder="Enter target concentration"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">ppm</span>
                              </div>
                              <div className="mt-4 space-y-2">
                                <p className="text-sm text-gray-600">Or calculate from:</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">LEL (%)</label>
                                    <input
                                      type="number"
                                      onChange={handleLELInput}
                                      className="w-full p-2 text-sm border rounded focus:ring-1 focus:ring-blue-500"
                                      placeholder="LEL"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">TLV (ppm)</label>
                                    <input
                                      type="number"
                                      onChange={handleTLVInput}
                                      className="w-full p-2 text-sm border rounded focus:ring-1 focus:ring-blue-500"
                                      placeholder="TLV"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Navigation Buttons */}
                        <div className="flex justify-between mt-8">
                          <button 
                            type="button" 
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            onClick={prevTab}
                          >
                            Previous
                          </button>
                          <button 
                            type="button" 
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            onClick={nextTab}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Tab 4: Review & Calculate */}
                    {activeTab === 3 && (
                      <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                          Review & Calculate
                        </h2>
                        
                        <div className="space-y-6">
                          {/* Enclosure Conditions Summary */}
                          <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                            <h3 className="text-lg font-medium text-gray-800 mb-4">Enclosure Conditions</h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm font-medium text-gray-600">Volume:</p>
                                <p className="text-base font-semibold">{calculateVolume() || formData.volume} {formatVolumeUnit(formData.volumeUnit)}</p>
                              </div>
                              
                              <div>
                                <p className="text-sm font-medium text-gray-600">Mixing Condition:</p>
                                <p className="text-base font-semibold">
                                  {formData.mixingCondition === 2 ? 'Ideal' : formData.mixingCondition === 4 ? 'Moderate' : 'Poor'}
                                </p>
                              </div>
                              
                              {formData.length && formData.breadth && formData.height && (
                                <div>
                                  <p className="text-sm font-medium text-gray-600">Dimensions:</p>
                                  <p className="text-base font-semibold">{formData.length}{formData.lengthUnit} × {formData.breadth}{formData.breadthUnit} × {formData.height}{formData.heightUnit}</p>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Chemical Details Summary */}
                          <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                            <h3 className="text-lg font-medium text-gray-800 mb-4">Chemical Details</h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm font-medium text-gray-600">Chemical Name:</p>
                                <p className="text-base font-semibold">{formData.chemicalName}</p>
                              </div>
                              
                              <div>
                                <p className="text-sm font-medium text-gray-600">Molecular Weight:</p>
                                <p className="text-base font-semibold">{formData.molecularWeight} g/mol</p>
                              </div>
                              
                              <div>
                                <p className="text-sm font-medium text-gray-600">Physical State:</p>
                                <p className="text-base font-semibold capitalize">{formData.physicalState}</p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Calculate Button */}
                          <div className="text-center">
                            <button 
                              type="submit"
                              className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-lg font-medium"
                            >
                              Calculate Ventilation Requirements
                            </button>
                          </div>
                        </div>
                        
                        {/* Navigation Buttons */}
                        <div className="flex justify-between mt-8">
                          <button 
                            type="button" 
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            onClick={prevTab}
                          >
                            Previous
                          </button>
                          <button 
                            type="button" 
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            onClick={nextTab}
                          >
                            View Results
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Tab 5: Results */}
                    {activeTab === 4 && (
                      <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                          Calculation Results
                        </h2>
                        
                        {results ? (
                          <div className="space-y-6">
                            {/* Main Results */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              <div className="bg-blue-50 p-6 rounded-lg text-center grid gap-4">
                                <h4 className="font-medium text-blue-800 mb-2">Ventilation Rate</h4>
                                <p className="text-3xl font-bold text-blue-900">{results.ventilationRate}</p>
                                {/* <p className="text-lg text-blue-700">{formatResultVolumeFlowRateUnit(resultVolumeFlowRateUnit)}</p> */}
                                <div className="max-w-xs m-auto">
                                  <select 
                                    className="w-full p-3 text-center border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" 
                                    value={resultVolumeFlowRateUnit}
                                    onChange={(e) => updateResultWithRateUnit(e.target.value)}
                                  >
                                    <option value="m3/min">m³/min</option>
                                    <option value="m3/h">m³/hr</option>
                                    <option value="m3/s">m³/sec</option>
                                    <option value="l/min">L/min</option>
                                    <option value="l/h">L/hr</option>
                                    <option value="l/s">L/sec</option>
                                    <option value="ft3/min">ft³/min</option>
                                    <option value="ft3/h">ft³/hr</option>
                                    <option value="kl/h">kL/hr</option>
                                    <option value="kl/s">kL/sec</option>
                                    <option value="kl/min">kL/min</option>
                                  </select>
                                </div>
                              </div>
                              
                              <div className="bg-green-50 p-6 rounded-lg text-center grid gap-4">
                                <h4 className="font-medium text-green-800 mb-2">Air Changes per Hour</h4>
                                <p className="text-3xl font-bold text-green-900">{(results.airChangesPerHour*60).toFixed(2)}</p>
                                <p className="text-lg text-green-700">ACH</p>
                              </div>

                              {results.airChangesPerHour*60 > 15 && (
                                <div className="bg-yellow-50 p-6 rounded-lg text-center grid gap-4">
                                  <h4 className="font-medium text-yellow-800 mb-2">Recommendation</h4>
                                  <p className="text-lg text-yellow-700">Consider using local exhaust, general room ventilation might not be enough.</p>
                                </div>
                              )}

                              <div className="bg-gray-50 p-6 rounded-lg text-center grid gap-4">
                                <h4 className="font-medium text-gray-800 mb-2">Volume</h4>
                                <p className="text-3xl font-bold text-gray-900">{results.volume}</p>
                                <p className="text-lg text-gray-700">m³</p>
                              </div>
                            </div>
                            
                          </div>
                        ) : (
                          <div className="text-center text-gray-500 py-12">
                            <i className="fas fa-calculator text-6xl mb-4 opacity-50"></i>
                            <h3 className="text-xl font-medium mb-2">No Results Available</h3>
                            <p className="mb-6">Complete the form and calculate to see your ventilation requirements.</p>
                            <button 
                              type="button"
                              onClick={() => setActiveTab(3)}
                              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              Go Back to Calculate
                            </button>
                          </div>
                        )}
                        
                        {/* Navigation */}
                        <div className="flex justify-between mt-8">
                          <button 
                            type="button" 
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            onClick={prevTab}
                          >
                            Previous
                          </button>
                          <button 
                            type="button" 
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors opacity-50 cursor-not-allowed"
                            disabled
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VentilationCalculator;
